import { useState, useEffect, useMemo } from 'react';
import { toast } from "react-toastify";
import { extractOrderItems, getLogin } from '../../../utils/StringUtils';
import { getBrazilDateString } from '../../../utils/DateUtils';
import { useOrderUpdates } from './useOrderUpdates';
import MenuService from '../../../rest/menuService';
import ProductService from '../../../rest/productService';
import ClientService from '../../../rest/clientService';
import OrderService from '../../../rest/orderService';
import NotificationsService from '../../../rest/notificationService';
import ComboService from '../../../rest/comboService';

export const useOrdersLogic = () => {
    const [allOrders, setAllOrders] = useState([]);
    const [recipeData, setRecipesData] = useState([]);
    const [productsData, setProductsData] = useState([]);
    const [combosData, setCombosData] = useState([]);
    const [preferences, setPreferences] = useState({});
    const [loading, setLoading] = useState(false);
    const [ordersStatus, setOrdersStatus] = useState('inProgress');
    const [gradeFilter, setGradeFilter] = useState('');
    const [itemFilter, setItemFilter] = useState('');
    const [search, setSearch] = useState('');

    const [dateRange, setDateRange] = useState(() => {
        const today = getBrazilDateString();
        return {
            start: `${today}T00:00:00`,
            end: `${today}T23:59:59`
        };
    });

    const getDecomposedNames = (itemName) => {
        if (!itemName) return [];
        const trimmed = itemName.trim();
        if (!trimmed.toLowerCase().includes(' e ')) return [trimmed];

        const normalized = trimmed.toLowerCase();
        const isKnownProduct =
            recipeData?.some(r => r.name?.trim().toLowerCase() === normalized) ||
            productsData?.some(p => p.name?.trim().toLowerCase() === normalized);

        if (isKnownProduct) return [trimmed];
        return trimmed.split(/ e /i).map(name => name.trim());
    };

    const parseComboComponents = (comboItemsList) => {
        if (!comboItemsList) return [];
        return comboItemsList.split(',').map(item => {
            const parts = item.trim().split(' x');
            return { name: parts[0]?.trim() || '', quantity: parseInt(parts[1]) || 1 };
        }).filter(({ name }) => name);
    };

    const checkItemNeedsPrep = (itemName, type = '') => {
        if (type === 'combo') {
            const normalized = itemName.trim().toLowerCase();
            const combo = combosData?.find(c => c.name?.trim().toLowerCase() === normalized);
            if (!combo) return false;
            return parseComboComponents(combo.itemsList).some(({ name: compName }) => {
                const n = compName.toLowerCase();
                return recipeData?.some(r => r.name?.trim().toLowerCase() === n && r.needsPreparation === true) ||
                       productsData?.some(p => p.name?.trim().toLowerCase() === n && p.needsPreparation === true);
            });
        }

        const namesToCheck = getDecomposedNames(itemName);
        return namesToCheck.some(name => {
            const normalizedName = name.toLowerCase();
            const recipeMatch = recipeData?.find(r => r.name?.trim().toLowerCase() === normalizedName);
            const productMatch = productsData?.find(p => p.name?.trim().toLowerCase() === normalizedName);
            return (recipeMatch?.needsPreparation === true) || (productMatch?.needsPreparation === true);
        });
    };

    const orderHasPrepItems = (order) => {
        const items = extractOrderItems(order.items_list);
        return items.some(item => checkItemNeedsPrep(item.name, item.type));
    };

    const fetchOrders = async (range) => {
        setLoading(true);
        try {
            const MAX_ITEMS = 9999;
            const response = await OrderService.getAllOrders(0, MAX_ITEMS, range);
            setAllOrders(response.content || []);
        } catch (error) {
            console.error("Erro na busca:", error);
            toast.error("Erro ao carregar pedidos.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const initData = async () => {
            setLoading(true);
            try {
                const lastStatus = sessionStorage.getItem("lastOrdersStatus");
                if (lastStatus) setOrdersStatus(lastStatus);

                const MAX_ITEMS = 9999;
                const [recipes, products, prefs, combos] = await Promise.all([
                    MenuService.getAllRecipes(0, MAX_ITEMS),
                    ProductService.getActiveProducts(0, MAX_ITEMS),
                    ClientService.getClientAdminPreferences(),
                    ComboService.getAllCombos({ page: 0, size: MAX_ITEMS })
                ]);

                setRecipesData(recipes.content || []);
                setProductsData(products.content || []);
                setCombosData(combos.content || []);
                setPreferences(prefs || {});

                await fetchOrders(dateRange);
            } catch (error) {
                console.error(error);
                toast.error("Erro ao carregar dados iniciais.");
            } finally {
                setLoading(false);
            }
        };
        initData();
    }, []);

    useOrderUpdates({
        clientCode: sessionStorage.getItem("clientCode"),
        setAllOrders: setAllOrders
    });

    const totalItemsInProgress = useMemo(() => {
        const ordersInKitchen = allOrders.filter(o => {
            if (o.status !== 'COMPLETED') return false;
            if (gradeFilter && o.grade !== gradeFilter) return false;
            return orderHasPrepItems(o);
        });
        const itemMap = new Map();

        ordersInKitchen.forEach(order => {
            extractOrderItems(order.items_list).forEach(({ name, quantity, type }) => {
                if (type === 'combo') {
                    const normalized = name.trim().toLowerCase();
                    const combo = combosData?.find(c => c.name?.trim().toLowerCase() === normalized);
                    if (!combo) return;
                    parseComboComponents(combo.itemsList).forEach(({ name: compName, quantity: compQty }) => {
                        if (checkItemNeedsPrep(compName)) {
                            const key = compName.trim().toLowerCase();
                            const current = itemMap.get(key) || { name: compName.trim(), quantity: 0 };
                            itemMap.set(key, { name: compName.trim(), quantity: current.quantity + quantity * compQty });
                        }
                    });
                } else {
                    const individualItems = getDecomposedNames(name);
                    individualItems.forEach(partName => {
                        if (checkItemNeedsPrep(partName)) {
                            const normalized = partName.trim();
                            const key = normalized.toLowerCase();
                            const current = itemMap.get(key) || { name: normalized, quantity: 0 };
                            itemMap.set(key, { name: normalized, quantity: current.quantity + quantity });
                        }
                    });
                }
            });
        });
        return Array.from(itemMap.values());
    }, [allOrders, recipeData, productsData, combosData, gradeFilter]);

    const filteredOrders = useMemo(() => {
        if (!allOrders) return [];

        let filtered = allOrders.filter(o => {
            const hasPrep = orderHasPrepItems(o);
            if (ordersStatus === 'toDeliver') return o.status === 'READYTODELIVER' || (o.status === 'COMPLETED' && !hasPrep);
            if (ordersStatus === 'inProgress') return o.status === 'PROCESSINGPAYMENT' || (o.status === 'COMPLETED' && hasPrep);
            if (ordersStatus === 'done') return ['FINISHED', 'CANCELED'].includes(o.status);
            return true;
        });

        if (search) {
            const term = search.toLowerCase();
            filtered = filtered.filter(order =>
                order.order_id.toString().includes(term) ||
                order.client_id.toLowerCase().includes(term) ||
                (order.grade && order.grade.toLowerCase().includes(term))
            );
        }

        if (gradeFilter) filtered = filtered.filter(o => o.grade === gradeFilter);
        if (itemFilter) filtered = filtered.filter(o => o.items_list.toLowerCase().includes(itemFilter.toLowerCase()));

        return filtered.sort((a, b) => b.order_id - a.order_id);
    }, [allOrders, ordersStatus, gradeFilter, itemFilter, search, recipeData, productsData, combosData]);

    const groupedOrders = useMemo(() => {
        if (ordersStatus !== 'done') return null;

        return filteredOrders.reduce((groups, order) => {
            const date = new Date(order.last_update || order.created_at).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
            if (!groups[date]) groups[date] = [];
            groups[date].push(order);
            return groups;
        }, {});
    }, [filteredOrders, ordersStatus]);

    const actions = {
        handleStatusChange: (newStatus) => {
            setOrdersStatus(newStatus);
            sessionStorage.setItem("lastOrdersStatus", newStatus);
        },
        handleSearchChange: (value) => setSearch(value),
        handleDateRangeChange: (newRange) => {
            setDateRange(newRange);
            fetchOrders(newRange);
        },
        finishOrder: async (order) => {
            const updated = { ...order, status: "FINISHED", last_update: new Date() };
            setAllOrders(prev => prev.map(o => o.order_id === order.order_id ? updated : o));
            try {
                await OrderService.updateOrderStatus(updated);
                toast.success("Pedido finalizado!");
            } catch (e) { toast.error("Erro ao finalizar."); }
        },
        deliverOrder: async (order) => {
            const updated = { ...order, status: 'READYTODELIVER', clientCode: sessionStorage.getItem('clientCode'), last_update: new Date() };
            setAllOrders(prev => prev.map(o => o.order_id === order.order_id ? updated : o));
            try {
                await OrderService.updateOrderStatus(updated);
                toast.success("Enviado para Entrega!");
            } catch (e) { toast.error("Erro ao atualizar status."); }
        },
        updateOrderInList: (orderId, updates) => {
            setAllOrders(prev => prev.map(o =>
                o.order_id === orderId ? { ...o, ...updates } : o
            ));
        },
        cancelOrder: async (order, motive) => {
            setLoading(true);
            try {
                const clientCode = sessionStorage.getItem('clientCode');
                const clientLogin = getLogin(order.client_id);

                const updatedOrder = {
                    ...order,
                    status: 'CANCELED',
                    clientCode: clientCode,
                    last_update: new Date(),
                    observations: motive
                };

                if (motive !== "Produto fora de estoque.") {
                    try {
                        const items = extractOrderItems(order.items_list);

                        const returnPromises = items.map(item => {
                            let itemType = item.type;

                            if (!itemType) {
                                const normalizedName = item.name.trim().toLowerCase();
                                const isRecipe = recipeData.some(r => r.name?.toLowerCase() === normalizedName);
                                const isProduct = productsData.some(p => p.name?.toLowerCase() === normalizedName);

                                itemType = isRecipe ? 'recipe' : (isProduct ? 'product' : 'combo');
                            }

                            return MenuService.returnItems({
                                id: item.id,
                                name: item.name,
                                quantity: item.quantity,
                                type: itemType, 
                                client_code: clientCode
                            });
                        });

                        await Promise.all(returnPromises);
                    } catch (e) {
                        console.error("Erro ao devolver itens:", e);
                    }
                }

                await OrderService.updateOrderStatus(updatedOrder);

                if (preferences?.enable_send_notifications) {
                    await NotificationsService.sendEmailNotificationForOrderCancelation(clientLogin, order);
                }

                setAllOrders(prev => prev.map(o => o.order_id === order.order_id ? updatedOrder : o));
                toast.success("Pedido cancelado com sucesso!");
            } catch (error) {
                toast.error("Não foi possível completar o cancelamento.");
            } finally {
                setLoading(false);
            }
        }
    };

    const kitchenItemsFiltered = itemFilter
        ? totalItemsInProgress.filter(i => i.name.toLowerCase() === itemFilter.toLowerCase())
        : totalItemsInProgress;

    return {
        data: {
            allOrders,
            filteredOrders,
            groupedOrders,
            totalItemsInProgress: kitchenItemsFiltered,
            uniqueGrades: [...new Set(allOrders.map(o => o.grade).filter(Boolean))],
            uniqueOrderItems: totalItemsInProgress.map(i => ({ name: i.name })).sort((a, b) => a.name.localeCompare(b.name)),
            totalQuantityForFilteredItem: !itemFilter ? 0 : totalItemsInProgress.find(i => i.name.toLowerCase() === itemFilter.toLowerCase())?.quantity || 0,
            ordersStatus,
            loading,
            preferences,
            dateRange
        },
        filters: { gradeFilter, setGradeFilter, itemFilter, setItemFilter, search },
        actions,
        helpers: { orderHasPrepItems }
    };
};